import { useMutation } from "@apollo/client/react";
import { App, Button, Flex, Popover, Space, Tooltip, Typography } from "antd";
import { DisconnectOutlined, InfoCircleOutlined, QuestionCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { useCallback, useId, useRef } from "react";
import { useEnterToNext } from "../../../hooks/useEnterToNext.ts";
import { useTranslation } from "react-i18next";
import {
  UnitType,
  UpdateIngredientDocument,
  type UpdateIngredientInput,
} from "../../../gql/generated/graphqlSchema.tsx";
import { useSaving } from "../../../hooks/useSaving.tsx";
import { LocaleNumberInput } from "../../../components/LocaleNumberInput.tsx";
import { NutrientValue } from "../../../components/NutrientValue.tsx";
import { NutritionInfo } from "../../../components/NutritionInfo.tsx";
import { QuantityUnitInput } from "../../../components/QuantityUnitInput.tsx";
import { genericIngredientDisplayName } from "../../../utils/genericIngredient.ts";
import { useIngredient } from "../IngredientContext.tsx";
import { ComparePhotosButton } from "../ComparePhotosButton.tsx";
import { ImportNutritionModal } from "./ImportNutritionModal.tsx";
import { css } from "@linaria/core";
import { clickable } from "../../../styles/shared.styles.ts";

const nutrientInputStyle = css`
  width: 140px;
`;

const nutritionTable = css`
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid var(--ant-color-border-secondary);
  border-radius: var(--ant-border-radius-lg);
  overflow: hidden;

  th,
  td {
    border-bottom: 1px solid var(--ant-color-border-secondary);
    border-right: 1px solid var(--ant-color-border-secondary);
    padding: var(--ant-padding-xs) var(--ant-padding-sm);

    &:last-child {
      border-right: none;
    }
  }

  tr:last-child th,
  tr:last-child td {
    border-bottom: none;
  }

  th {
    background: var(--ant-color-bg-layout);
    text-align: left;
    font-weight: normal;
  }

  th:not(:first-child),
  td:not(:first-child) {
    text-align: right;
  }

  tbody tr:first-child th,
  tbody tr:first-child td {
    border-top: 3px solid var(--ant-color-border);
  }
`;

const subLabel = css`
  padding-left: var(--ant-padding-lg) !important;
`;

const per100gCol = css`
  white-space: nowrap;
  color: var(--ant-color-text-secondary);
`;

type NutrientField = "fat" | "saturatedFat" | "carbohydrate" | "sugars" | "fibre" | "protein" | "salt" | "alcohol";

const NUTRIENT_FIELDS: readonly NutrientField[] = [
  "fat",
  "saturatedFat",
  "carbohydrate",
  "sugars",
  "fibre",
  "protein",
  "salt",
  "alcohol",
];

const SUB_FIELDS = new Set<NutrientField>(["saturatedFat", "sugars"]);

export const IngredientNutritionTab = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const { ingredient, setIngredient, units } = useIngredient();
  const [updateIngredient] = useMutation(UpdateIngredientDocument);
  const { trackSaving } = useSaving();
  const id = useId();

  const np = ingredient.referenceNutritionProfile;
  const per100g = ingredient.nutritionProfile;
  const gi = ingredient.genericIngredient;
  const weightUnits = units.filter((u) => u.unitType !== UnitType.Volume);
  const gramsUnitId = units.find((u) => u.code === "g")?.id;
  const showPer100g = ingredient.nutritionReferenceGrams !== 100;

  const saveField = useCallback(
    (input: UpdateIngredientInput) => {
      trackSaving(() =>
        updateIngredient({
          variables: { id: ingredient.id, input },
        }).then((result) => setIngredient(result.data?.updateIngredient.refreshedIngredient)),
      );
    },
    [ingredient.id, updateIngredient, trackSaving, setIngredient],
  );

  const isOutOfSync = gi != null && NUTRIENT_FIELDS.some((f) => per100g[f] !== gi.nutritionProfile[f]);

  const handleUnlink = useCallback(() => {
    saveField({ genericIngredientId: null });
    message.success(t("ingredients.nutrition.unlinkDone"));
  }, [saveField, message, t]);

  const handleSync = useCallback(() => {
    if (gi == null) return;
    const gnp = gi.nutritionProfile;
    saveField({
      fat: gnp.fat,
      saturatedFat: gnp.saturatedFat,
      carbohydrate: gnp.carbohydrate,
      sugars: gnp.sugars,
      fibre: gnp.fibre,
      protein: gnp.protein,
      salt: gnp.salt,
      alcohol: gnp.alcohol,
      nutritionReferenceQuantity: 100,
      nutritionReferenceUnitId: gramsUnitId,
    });
    message.success(t("ingredients.nutrition.syncDone"));
  }, [gi, gramsUnitId, saveField, message, t]);

  const lang = i18n.language;
  const energyDisplay = (
    <>
      <NutrientValue language={lang} type="energy" unit="kJ" value={np.energyKj} /> /{" "}
      <NutrientValue language={lang} type="energy" unit="kcal" value={np.energyKcal} />
    </>
  );
  const energyPer100g = (
    <>
      <NutrientValue language={lang} type="energy" unit="kJ" value={per100g.energyKj} /> /{" "}
      <NutrientValue language={lang} type="energy" unit="kcal" value={per100g.energyKcal} />
    </>
  );

  const tableRef = useRef<HTMLDivElement>(null);
  const enterToNext = useEnterToNext(tableRef);

  const nutrientInput = (field: NutrientField, step = 0.1) => (
    <LocaleNumberInput
      changeOnWheel
      className={nutrientInputStyle}
      classNames={{ input: "marker" }}
      defaultValue={np[field] ?? undefined}
      id={`${id}-${field}}`}
      onCommit={(value) => saveField({ [field]: value })}
      onPressEnter={enterToNext}
      step={step}
      suffix="g"
    />
  );

  const fmtPer100g = (field: NutrientField) => (
    <NutrientValue language={lang} type={field} unit="g" value={per100g[field]} />
  );

  return (
    <Flex gap="medium" vertical>
      <Flex align="center" gap="small" wrap>
        {!gi ? <ImportNutritionModal /> : null}
        <ComparePhotosButton />
      </Flex>
      <Flex align="center" gap="small" wrap>
        {gi ? (
          <>
            <Typography.Text>
              {t("ingredients.nutrition.importedFrom", {
                name: genericIngredientDisplayName(gi, i18n.language),
              })}
            </Typography.Text>
            <Popover
              content={<NutritionInfo nutritionProfile={gi.nutritionProfile} />}
              destroyOnHidden
              title={genericIngredientDisplayName(gi, i18n.language)}
            >
              <InfoCircleOutlined className={clickable} />
            </Popover>
            <Space wrap>
              {isOutOfSync ? (
                <Tooltip destroyOnHidden title={t("ingredients.nutrition.sync")}>
                  <Button color="primary" icon={<SyncOutlined />} onClick={handleSync} variant="outlined" />
                </Tooltip>
              ) : null}
              <Tooltip destroyOnHidden title={t("ingredients.nutrition.unlink")}>
                <Button danger icon={<DisconnectOutlined />} onClick={handleUnlink} variant="text" />
              </Tooltip>
            </Space>
          </>
        ) : null}
      </Flex>
      <div ref={tableRef}>
        <table className={nutritionTable}>
          <thead>
            <tr>
              <th>
                <strong>{t("ingredients.nutrition.valuesPerLabel")}</strong>
              </th>
              <th>
                <QuantityUnitInput
                  onChange={({ quantity, unitId }) =>
                    saveField({ nutritionReferenceQuantity: quantity, nutritionReferenceUnitId: unitId })
                  }
                  units={weightUnits}
                  value={{
                    quantity: ingredient.nutritionReferenceQuantity,
                    unitId: ingredient.nutritionReferenceUnitId,
                  }}
                />
              </th>
              {showPer100g ? <th className={per100gCol}>{t("ingredients.nutrition.perHundredGramsShort")}</th> : null}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>
                <Space>
                  {t("ingredients.nutrition.energy")}
                  <Tooltip destroyOnHidden title={t("ingredients.nutrition.energyTooltip")}>
                    <QuestionCircleOutlined />
                  </Tooltip>
                </Space>
              </th>
              <td>
                <strong>{energyDisplay}</strong>
              </td>
              {showPer100g ? (
                <td className={per100gCol}>
                  <strong>{energyPer100g}</strong>
                </td>
              ) : null}
            </tr>
            {NUTRIENT_FIELDS.map((field) => (
              <tr key={field}>
                <th className={SUB_FIELDS.has(field) ? subLabel : undefined}>
                  <label htmlFor={`${id}-${field}`}>{t(`ingredients.nutrition.${field}`)}</label>
                </th>
                <td>{nutrientInput(field, field === "salt" ? 0.01 : 0.1)}</td>
                {showPer100g ? <td className={per100gCol}>{fmtPer100g(field)}</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Flex>
  );
};
